import {ChangeDetectionStrategy, Component, effect, input, OnDestroy, output, signal} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {TranslateModule} from "@ngx-translate/core";

const debug = initDebugger('MikrofonPulsKomponent');

/**
 * Viser lydnivået fra en mikrofon som en pulserende indikator, slik at brukeren ser at mikrofonen fanger opp lyd.
 *
 * Komponenten tar ikke opp lyd – den lytter kun så lenge den er synlig, og frigir mikrofonen når den fjernes
 * eller når det velges en annen mikrofon.
 */
@Component({
    selector: 'mikrofon-puls',
    templateUrl: 'mikrofon-puls.html',
    styleUrl: 'mikrofon-puls.scss',
    imports: [TranslateModule],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MikrofonPulsKomponent implements OnDestroy {
    /** deviceId til mikrofonen det skal lyttes på, eller null for standardmikrofonen */
    deviceId = input<string | null>(null);
    vedFeil = output<Error>();

    lydnivaa = signal(0);

    private lydstrøm?: MediaStream;
    private lydkontekst?: AudioContext;
    private kilde?: MediaStreamAudioSourceNode;
    private analyser?: AnalyserNode;
    private lyddata?: Float32Array<ArrayBuffer>;
    private animasjonsId?: number;
    /** kjennetegner målingen som er i gang, slik at en oppstart som blir ferdig for sent ser at den er utdatert */
    private måling?: object;

    constructor() {
        //byttes mikrofonen, lyttes det på den nye i stedet
        effect(() => void this.lyttPå(this.deviceId()));
    }

    ngOnDestroy(): void {
        this.frigiRessurser();
    }

    //private hjelpemetoder

    private async lyttPå(deviceId: string | null): Promise<void> {
        this.frigiRessurser();
        if (erNil(navigator.mediaDevices?.getUserMedia) || erNil(globalThis.AudioContext)) return;

        const måling = this.måling = {};
        try {
            const lydstrøm = await navigator.mediaDevices.getUserMedia(
              {audio: erIkkeNil(deviceId) ? {deviceId: {exact: deviceId}} : true});

            //mikrofonen kan være byttet, eller komponenten fjernet, mens vi ventet på tilgang
            if (this.måling !== måling) {
                lydstrøm.getTracks().forEach(spor => spor.stop());
                return;
            }

            this.lydstrøm = lydstrøm;
            this.initLydanalyse();
            this.animasjonsId = requestAnimationFrame(() => this.måleLydnivå());
        } catch (feil: any) {
            //en sen feil hører til en måling som allerede er avsluttet eller erstattet
            if (this.måling !== måling) return;

            debug('kunne ikke lytte på mikrofonen', feil);
            this.frigiRessurser();
            this.vedFeil.emit(feil instanceof Error ? feil : new Error(String(feil)));
        }
    }

    /** Setter opp Web Audio API for å kunne måle lydnivået */
    private initLydanalyse(): void {
        this.lydkontekst = new AudioContext();
        this.kilde = this.lydkontekst.createMediaStreamSource(this.lydstrøm!);
        this.analyser = this.lydkontekst.createAnalyser();
        this.analyser.fftSize = 512;
        this.kilde.connect(this.analyser);
        this.lyddata = new Float32Array(this.analyser.fftSize);
    }

    /** Måle-løkken, som oppdaterer lydnivået for hver frame */
    private måleLydnivå(): void {
        if (erNil(this.analyser) || erNil(this.lyddata)) return;

        this.analyser.getFloatTimeDomainData(this.lyddata);
        //1.4 er en ren visningsforsterkning, slik at normal tale gir godt utslag – som i Mikrofonopptak
        this.lydnivaa.set(Math.min(1, this.måltRms(this.lyddata) * 1.4));

        this.animasjonsId = requestAnimationFrame(() => this.måleLydnivå());
    }

    private måltRms(lyddata: Float32Array<ArrayBuffer>): number {
        let sumKvadrater = 0;
        for (const verdi of lyddata)
            sumKvadrater += verdi * verdi;

        return Math.sqrt(sumKvadrater / lyddata.length);
    }

    /** Stopper måle-løkken og frigir mikrofon og lydkontekst */
    private frigiRessurser(): void {
        this.måling = undefined;

        if (erIkkeNil(this.animasjonsId)) {
            cancelAnimationFrame(this.animasjonsId);
            this.animasjonsId = undefined;
        }

        this.kilde?.disconnect();
        this.kilde = undefined;
        this.analyser?.disconnect();
        this.analyser = undefined;
        this.lyddata = undefined;

        void this.lydkontekst?.close();
        this.lydkontekst = undefined;

        this.lydstrøm?.getTracks().forEach(spor => spor.stop());
        this.lydstrøm = undefined;

        this.lydnivaa.set(0);
    }
}
